import { memo, useEffect, useMemo, useState } from 'react';
import { Upload, Sparkles, Eye, Zap, Video, Wand2, Eraser, GripVertical, Users, Volume2, UserCircle, Clapperboard, Film, Music, Image, Mic2, Star, Pencil, ChevronLeft, ChevronRight, Search, X, QrCode, Gem, AudioLines, Languages, Camera } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';

// E5 · flag CAPTURE_FRAME_NODE: apaga la entrada de la paleta (el nodo ya
// creado en un proyecto sigue renderizando igual; el registro no se toca).
const CAPTURE_FRAME_AVAILABLE =
  String(process.env.REACT_APP_CAPTURE_FRAME_NODE ?? 'true').toLowerCase() !== 'false';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../ui/tooltip';
import { NodeTooltipBody } from '../nodes/NodeTooltip';
import { useToolsStore } from '../../store/toolsStore';

// Tools organized by category and subcategory
const tools = {
  entrada: [
    {
      id: 'uploadImage',
      name: 'Subir Imagen',
      description: 'Carga una imagen de entrada',
      icon: Image,
      available: true,
    },
    {
      id: 'uploadVideo',
      name: 'Subir Video',
      description: 'Carga un video de entrada',
      icon: Film,
      available: true,
    },
    {
      id: 'uploadAudio',
      name: 'Subir Audio',
      description: 'Carga un audio de entrada',
      icon: Music,
      available: true,
    },
  ],
  procesamiento: {
    imagen: [
      {
        id: 'imageEnhancement',
        name: 'Mejorar Imagen',
        description: 'Restaura y mejora calidad',
        icon: Sparkles,
        available: true,
      },
      {
        id: 'nanoBanana',
        name: 'Nano Banana',
        description: 'Genera y edita imágenes con IA',
        icon: Wand2,
        available: true,
      },
      {
        id: 'bgRemoval',
        name: 'Remover Fondo',
        description: 'Elimina el fondo con IA',
        icon: Eraser,
        available: true,
      },
      {
        id: 'upscale',
        name: 'Upscale',
        description: 'Aumenta resolución y nitidez',
        icon: Zap,
        available: true,
      },
      ...(CAPTURE_FRAME_AVAILABLE ? [{
        id: 'captureFrame',
        name: 'Capturar Frame',
        description: 'Captura un cuadro exacto del video como imagen (gratis, en tu navegador)',
        icon: Camera,
        available: true,
      }] : []),
      {
        id: 'styleTransfer',
        name: 'Style Transfer',
        description: 'Transfiere estilo artístico',
        icon: Wand2,
        available: true,
      },
      {
        id: 'frameExtractor',
        name: 'Frame Extractor',
        description: 'Extrae frame de un video',
        icon: Film,
        available: true,
      },
      {
        id: 'imageEdit',
        name: 'Image Edit',
        description: 'Edición rápida con IA',
        icon: Pencil,
        available: true,
      },
      {
        id: 'banana2pro',
        name: 'Editor Banana 2 Pro',
        description: 'Generación + edición multi-imagen',
        icon: Star,
        available: true,
      },
      {
        id: 'imagen4Ultra',
        name: 'Image - 4Ultra',
        description: 'Máxima calidad desde texto (Imagen 4 Ultra)',
        icon: Gem,
        available: true,
      },
      {
        id: 'genQR',
        name: 'Generador QR',
        description: 'QR personalizable con logo opcional',
        icon: QrCode,
        available: true,
      },
    ],
    video: [
      {
        id: 'videoGeneration',
        name: 'Crear Video',
        description: 'Imagen a video cinematográfico',
        icon: Video,
        available: true,
      },
      {
        id: 'videoWithSound',
        name: 'Video con Sonido',
        description: 'Video con música y efectos',
        icon: Volume2,
        available: true,
      },
      {
        id: 'seedance25',
        name: 'Video seedance-2.5',
        description: 'Flagship multimodal: 30s con audio nativo',
        icon: Clapperboard,
        available: true,
      },
      {
        id: 'pixverseV6',
        name: 'Pixverse V6',
        description: 'Cinematográfico: multi-shot y audio',
        icon: Film,
        available: true,
      },
      {
        id: 'lipSync',
        name: 'LipSinc',
        description: 'Sincroniza labios con un audio (Sync 2.0)',
        icon: Mic2,
        available: true,
      },
      {
        id: 'dreamActor',
        name: 'DreamActor',
        description: 'Transfiere movimiento',
        icon: Clapperboard,
        available: true,
      },
      {
        id: 'fabricAvatar',
        name: 'Avatar Parlante',
        description: 'Imagen + audio = lip-sync',
        icon: UserCircle,
        available: true,
      },
      {
        id: 'klingAvatar',
        name: 'Avatar V2 Kling',
        description: 'Avatar realista con emociones',
        icon: Star,
        available: true,
      },
      {
        id: 'avatarFast',
        name: 'Avatar Fast',
        description: 'Avatar parlante veloz: guion o audio',
        icon: Zap,
        available: true,
      },
    ],
    sonido: [
      {
        id: 'lyria2',
        name: 'Lyria 2',
        description: 'Genera música con IA',
        icon: Mic2,
        available: true,
      },
      {
        id: 'qwen3tts',
        name: 'Qwen3 TTS',
        description: 'Texto a voz multilingüe',
        icon: Volume2,
        available: true,
      },
      {
        id: 'eleV2',
        name: 'EleV2',
        description: 'Voz ElevenLabs · 29 idiomas',
        icon: AudioLines,
        available: true,
      },
      {
        id: 'elTraductor',
        name: 'El_Traductor',
        description: 'Doblaje a 90+ idiomas conservando la voz',
        icon: Languages,
        available: true,
      },
      {
        id: 'musicPro',
        name: 'Music Pro',
        description: 'Canciones completas con o sin voces',
        icon: Music,
        available: true,
      },
      {
        id: 'lyria3pro',
        name: 'Lyria 3 Full Song',
        description: 'Canciones completas hasta 3 min',
        icon: Music,
        available: true,
      },
    ],
  },
  salida: [
    {
      id: 'outputPreview',
      name: 'Vista Previa',
      description: 'Visualiza y descarga',
      icon: Eye,
      available: true,
    },
  ],
};

const ToolItem = memo(({ tool, onDragStart, onClick }) => (
  <Tooltip delayDuration={500}>
    <TooltipTrigger asChild>
      <div
        className={`tool-item ${!tool.available ? 'disabled' : ''}`}
        draggable={tool.available}
        onDragStart={(e) => onDragStart(e, tool)}
        onClick={() => onClick(tool)}
        data-testid={`tool-${tool.id}`}
      >
        <div className="tool-drag-handle">
          <GripVertical size={12} />
        </div>
        <div className="tool-icon">
          <tool.icon size={18} />
        </div>
        <div className="tool-info">
          <span className="tool-name">{tool.name}</span>
          <span className="tool-description">{tool.description}</span>
        </div>
        {!tool.available && <span className="tool-badge">Pronto</span>}
      </div>
    </TooltipTrigger>
    <TooltipContent
      className="node-rich-tooltip"
      side="right"
      align="center"
      sideOffset={12}
      collisionPadding={16}
    >
      <NodeTooltipBody toolId={tool.id} />
    </TooltipContent>
  </Tooltip>
));

ToolItem.displayName = 'ToolItem';

const ToolPalette = memo(() => {
  const addNode = useWorkflowStore((state) => state.addNode);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('toolPaletteCollapsed') === '1'; } catch { return false; }
  });
  const [query, setQuery] = useState('');

  // Warm the tools cache so the first hover doesn't render the fallback body.
  const fetchTools = useToolsStore((s) => s.fetchTools);
  const toolsLoadedAt = useToolsStore((s) => s.loadedAt);
  useEffect(() => {
    if (!toolsLoadedAt) fetchTools();
  }, [toolsLoadedAt, fetchTools]);

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('toolPaletteCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const handleDragStart = (e, tool) => {
    if (!tool.available) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('application/reactflow', tool.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleToolClick = (tool) => {
    if (!tool.available) return;

    const position = {
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
    };
    addNode(tool.id, position);
  };

  // ── Filter tools by search query ─────────────────────
  const q = query.trim().toLowerCase();
  const matches = (tool) => {
    if (!q) return true;
    return (
      tool.id.toLowerCase().includes(q) ||
      tool.name.toLowerCase().includes(q) ||
      tool.description.toLowerCase().includes(q)
    );
  };
  const filtered = useMemo(() => ({
    entrada: tools.entrada.filter(matches),
    imagen: tools.procesamiento.imagen.filter(matches),
    video: tools.procesamiento.video.filter(matches),
    sonido: tools.procesamiento.sonido.filter(matches),
    salida: tools.salida.filter(matches),
  }), [q]);
  const totalMatches =
    filtered.entrada.length + filtered.imagen.length + filtered.video.length +
    filtered.sonido.length + filtered.salida.length;

  return (
    <div className={`tool-palette ${collapsed ? 'is-collapsed' : ''}`} data-testid="tool-palette">
      <button
        className="sidebar-toggle left"
        onClick={toggleCollapsed}
        title={collapsed ? 'Mostrar herramientas' : 'Ocultar herramientas'}
        aria-label={collapsed ? 'Expandir panel de herramientas' : 'Contraer panel de herramientas'}
        data-testid="tool-palette-toggle"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
      <div className="tool-palette-header">
        <h3>Herramientas</h3>
        <div className="tool-palette-search">
          <Search size={13} className="tps-icon" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar herramientas…"
            data-testid="tool-palette-search"
            aria-label="Buscar herramientas"
          />
          {query && (
            <button
              type="button"
              className="tps-clear"
              onClick={() => setQuery('')}
              aria-label="Limpiar búsqueda"
              data-testid="tool-palette-search-clear"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="tool-palette-content">
        {q && totalMatches === 0 && (
          <div className="tool-palette-empty" data-testid="tool-palette-empty">
            Sin resultados para <b>&ldquo;{query}&rdquo;</b>
          </div>
        )}

        {/* ENTRADA */}
        {filtered.entrada.length > 0 && (
        <div className="tool-category">
          <h4>Entrada</h4>
          <div className="tool-list">
            {filtered.entrada.map((tool) => (
              <ToolItem
                key={tool.id}
                tool={tool}
                onDragStart={handleDragStart}
                onClick={handleToolClick}
              />
            ))}
          </div>
        </div>
        )}

        {/* PROCESAMIENTO - IMAGEN */}
        {filtered.imagen.length > 0 && (
        <div className="tool-category">
          <h4>
            <span className="category-icon imagen">
              <Image size={12} />
            </span>
            Imagen
          </h4>
          <div className="tool-list">
            {filtered.imagen.map((tool) => (
              <ToolItem
                key={tool.id}
                tool={tool}
                onDragStart={handleDragStart}
                onClick={handleToolClick}
              />
            ))}
          </div>
        </div>
        )}

        {/* PROCESAMIENTO - VIDEO */}
        {filtered.video.length > 0 && (
        <div className="tool-category">
          <h4>
            <span className="category-icon video">
              <Film size={12} />
            </span>
            Video
          </h4>
          <div className="tool-list">
            {filtered.video.map((tool) => (
              <ToolItem
                key={tool.id}
                tool={tool}
                onDragStart={handleDragStart}
                onClick={handleToolClick}
              />
            ))}
          </div>
        </div>
        )}

        {/* PROCESAMIENTO - SONIDO */}
        {filtered.sonido.length > 0 && (
        <div className="tool-category">
          <h4>
            <span className="category-icon sonido">
              <Music size={12} />
            </span>
            Sonido
          </h4>
          <div className="tool-list">
            {filtered.sonido.map((tool) => (
              <ToolItem
                key={tool.id}
                tool={tool}
                onDragStart={handleDragStart}
                onClick={handleToolClick}
              />
            ))}
          </div>
        </div>
        )}

        {/* SALIDA */}
        {filtered.salida.length > 0 && (
        <div className="tool-category">
          <h4>Salida</h4>
          <div className="tool-list">
            {filtered.salida.map((tool) => (
              <ToolItem
                key={tool.id}
                tool={tool}
                onDragStart={handleDragStart}
                onClick={handleToolClick}
              />
            ))}
          </div>
        </div>
        )}

        <div className="tool-palette-footer">
          <p>Arrastra herramientas al canvas o haz clic para agregar</p>
        </div>
      </div>
    </div>
  );
});

ToolPalette.displayName = 'ToolPalette';

export default ToolPalette;
